import { faSpinner } from "@jewlr/pro-regular-svg-icons/faSpinner"
import { Box, FontAwesomeIcon, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import api from "apis"

import {
  FormContainer,
  FormWrapper,
  OrderNumber,
  Text,
  SubmitButton,
  CancelButton,
  StyledLink,
  ProductContainer,
  ProductImg,
  ProductTitle,
  ImgWrapper,
  InputWrapper,
} from "../components"
import Heading from "../heading"

import { createMarkup, SpinnerContainer } from "./components"

const EditConfirmation = ({
  canSubmit,
  data,
  setCanSubmit,
  setCompleted,
  setFinishedWizard,
  setValidChanges,
}) => {
  const [changes, setChanges] = useState({})
  const [processing, setProcessing] = useState(true)
  const [errors, setErrors] = useState([])

  useEffect(() => {
    if (data) {
      getChangeDetails(data)
    }
  }, [data])

  const getChangeDetails = async (data) => {
    const { order_params, ...no_order_params } = data
    setCanSubmit(false)

    await api
      .post("/secure/api/v10/order/process_order_change", {
        details: no_order_params,
        new_design: true,
        order_params: order_params,
      })
      .then((res) => {
        const { data } = res
        setValidChanges(data.valid_changes)
        setFinishedWizard(true)

        setChanges(data)

        setCanSubmit(true)
        setProcessing(false)
      })
      .catch((err) => {
        setProcessing(false)
        if (err?.errors) {
          setErrors(err.errors)
        }
      })
  }

  const processSubmit = async (e) => {
    e.preventDefault()

    await api
      .post("/secure/api/v10/order/process_order_change", {
        confirm: true,
        error_message: "",
        lir: changes.next_details.lir,
        olid: changes.next_details.olid,
        order_currency_code: changes.next_details.order_currency_code,
        order_number: changes.next_details.order_number,
        order_token: changes.next_details.order_token,
      })
      .then((res) => {
        const { data } = res
        setFinishedWizard(true)

        switch (data.action) {
          case "complete":
            setCompleted(true)
            break
          case "payment":
            window.location.href = data.url
            break
          default:
        }
      })
      .catch((err) => {
        if (err?.errors) {
          setErrors(err.errors)
        }
      })
  }

  return (
    // Review changes and proceed to finish or payment
    <FormContainer>
      <Heading rmaType="change-order" />
      <form>
        <Text fontSize="18px" fontWeight="700" lineHeight="20px" mb={2}>
          Please confirm the changes below:
        </Text>
        <Typography fontSize="18px" fontWeight="500" lineHeight="20px">
          Your Order Number:
        </Typography>
        <OrderNumber>{changes.next_details?.order_number}</OrderNumber>

        <ProductContainer>
          <ImgWrapper>
            {processing ? (
              <SpinnerContainer show={processing}>
                <FontAwesomeIcon icon={faSpinner} size="2x" spin />
              </SpinnerContainer>
            ) : Object.keys(changes).length > 0 ? (
              <ProductImg
                alt={changes.title}
                data-cy="order-actions-edit-confirmation-image"
                src={changes.image_url}
              />
            ) : null}
          </ImgWrapper>
          {!processing && Object.keys(changes).length > 0 && (
            <Box textAlign="left">
              <ProductTitle mb={3}>{changes.title}</ProductTitle>
              <Text mb="4px">Item Details:</Text>
              {(changes?.details || []).map((element, i) => {
                return (
                  <Text
                    dangerouslySetInnerHTML={createMarkup(element)}
                    key={i}
                    mb="2px"
                  />
                )
              })}
            </Box>
          )}
        </ProductContainer>

        {!processing && changes.box_addon && (
          <ProductContainer mb={4}>
            <ImgWrapper>
              <ProductImg
                alt={changes.box_addon.box_title}
                src={changes.box_addon.image}
              />
            </ImgWrapper>

            <Box textAlign="left">
              <ProductTitle mb={3}>
                {changes.box_addon.box_title} (+ {changes.box_addon.price})
              </ProductTitle>
            </Box>
          </ProductContainer>
        )}

        <Box mb={4}>
          <FormWrapper>
            {changes.new_date &&
              changes.old_date &&
              changes.new_date != changes.old_date && (
                <Text>
                  Your new Estimated Receive Date will be updated from{" "}
                  {changes.old_date} to <strong>{changes.new_date}</strong>.
                </Text>
              )}

            {changes.new_date && !changes.old_date && (
              <Text>
                Your new Estimated Receive Date is
                <strong>{changes.new_date}</strong>.
              </Text>
            )}
            {changes.factory_date && !changes.new_date && (
              <Text>
                Your new Estimated Ship Date will be{" "}
                <strong>{changes.factory_date}</strong>
              </Text>
            )}

            {changes.total_difference > 0.0 && (
              <div>
                <Text fontWeight="500">
                  There is a charge of {changes.total_difference_amount}{" "}
                  {!changes.price_includes_tax &&
                    "(plus applicable sales tax) "}
                  to proceed with this change. To complete this change, click
                  &quot;Proceed to Payment&quot; to submit payment.
                </Text>
                {false && changes.points_info.order_points_ratio && (
                  <Text>
                    {changes.points_info.points_credit} Jewls will also be
                    credited to your VIP Rewards account.
                  </Text>
                )}
              </div>
            )}
            {changes.total_difference < 0.0 && (
              <div>
                {changes.points_info.payment_difference < 0.0 && (
                  <Text>
                    You will receive a refund of{" "}
                    {changes.total_difference_amount}{" "}
                    {!changes.price_includes_tax &&
                      "(plus applicable sales tax) "}
                    when this change is processed. You should see this refund
                    appear in your account within 3-5 business days.
                  </Text>
                )}
                {false && changes.points_info.redemption_difference < 0.0 && (
                  <Text>
                    Your VIP Rewards account will be credited with{" "}
                    {changes.points_info.points_credit} Jewls (
                    {changes.points_info.dollar_credit})
                  </Text>
                )}
                {false &&
                  changes.points_info.order_points_ratio &&
                  changes.points_info.payment_difference < 0.0 && (
                    <Text>
                      {changes.points_info.deduction_credit} Jewls will also be
                      deducted from your VIP Rewards account.
                    </Text>
                  )}
              </div>
            )}
            {false && (
              <StyledLink
                data-lc="edit-confirmation"
                to={`/order/upgrade-shipping?order_number=${
                  changes.next_details ? changes.next_details.order_number : ""
                }`}
              >
                Want to upgrade your shipping?
              </StyledLink>
            )}
            <Text mb={5} mt={2}>
              Once{" "}
              {parseInt(changes.total_difference || 0) > 0.0
                ? "payment is received"
                : "your request is processed"}
              , you will receive a new confirmation email with your changes and
              updated shipping time. Please review this email to ensure your
              details are correct.
            </Text>
          </FormWrapper>
        </Box>
        {errors.length > 0 &&
          errors.map((error, i) => (
            <Typography
              color="error"
              dangerouslySetInnerHTML={{ __html: error }}
              key={`error-${i}`}
              mb={1}
            />
          ))}
        <InputWrapper>
          <SubmitButton
            data-cy="order-edit-confirm-btn"
            disabled={!canSubmit}
            onClick={processSubmit}
          >
            {changes.total_difference > 0.0
              ? "Proceed to Payment"
              : "Complete Request"}
          </SubmitButton>
        </InputWrapper>
        <InputWrapper>
          <CancelButton data-lc="edit-confirmation" to="/">
            Cancel and Continue Shopping
          </CancelButton>
        </InputWrapper>
      </form>
    </FormContainer>
  )
}

EditConfirmation.propTypes = {
  canSubmit: PropTypes.bool,
  data: PropTypes.object,
  setCanSubmit: PropTypes.func,
  setCompleted: PropTypes.func,
  setFinishedWizard: PropTypes.func,
  setValidChanges: PropTypes.func,
}

export default EditConfirmation
